import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
    Typography,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';
import { contentWidth, headerHeight } from '../../outside/components/Layout';
import { ContentCommenting } from '../../../common/components';

export const sideMenuWidth = 300;
const scrollerWidth = 16;

const styles = theme => ({
    root: {
        maxWidth: contentWidth,
        margin: theme.spacing(4, 'auto'),
        minHeight: `calc(100vh - ${headerHeight}px)`,
        padding: theme.spacing(0, 12),

        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(0, 4.5),
        },

        [theme.breakpoints.down('xs')]: {
            marginTop: 0,
            padding: theme.spacing(0, 1.5),
        },

        '@media print': {
            minHeight: 0,
        },
    },
    withSideMenu: {
        maxWidth: 650,
        padding: theme.spacing(0, 1),

        [theme.breakpoints.up('md')]: {
            marginRight: sideMenuWidth,
        },

        [theme.breakpoints.up('md')]: {
            marginRight: `calc((100% - ${theme.breakpoints.values.md - scrollerWidth}px)/2 + ${sideMenuWidth}px + ${theme.spacing(2)}px)`,
        },
    },
    sideMenu: {
        position: 'fixed',
        right: 0,
        top: headerHeight,
        bottom: 0,
        maxWidth: '100%',
        width: sideMenuWidth + scrollerWidth,
        overflowY: 'auto',
        marginRight: -scrollerWidth,
        zIndex: 100, // Must be larger than `stickyHeaderCell`

        [theme.breakpoints.down('sm')]: {
            display: 'none',
            boxShadow: '10px 10px 8px 10px #888888',
        },

        [theme.breakpoints.up('md')]: {
            width: `calc((100% - ${theme.breakpoints.values.md - scrollerWidth}px)/2 + ${sideMenuWidth}px)`,
            marginRight: 0,
        },

        '@media print': {
            display: 'none',
        },
    },
    sideMenuButton: {
        position: 'fixed',
        right: 0,
        bottom: 0,
        margin: '1rem',
        width: theme.spacing(6),
        height: theme.spacing(6),
        backgroundColor: theme.palette.grey[200],

        '&:hover': {
            backgroundColor: theme.palette.grey[300],
        },

        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
        zIndex: 100, // Must be >= than `sideMenu`

        '& span > svg': {
            position: 'absolute',

            '&:first-child': {
                transform: 'translate(0, -8px) rotate(180deg)',
                transition: 'transform 0.2s ease',
            },
            '&:last-child': {
                transform: 'translate(0, 8px)',
                transition: 'transform 0.2s ease',
            },
        },
        '&.active > span > svg': {
            '&:first-child': {
                transform: 'translate(0, 3.5px) rotate(180deg)',
            },
            '&:last-child': {
                transform: 'translate(0, -3.5px)',
            },
        },

        '@media print': {
            display: 'none',
        },
    },
    backdrop: {
        display: 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.5,
        backgroundColor: 'black',
        zIndex: 100,
    },
    showSideMenu: {
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    hideSideMenuButton: {
        display: 'none',
    },
    breadcrumb: {
        marginBottom: theme.spacing(1),

        [theme.breakpoints.down('xs')]: {
            marginBottom: 0,
        },
    },
    innerBreadcrumb: {
        marginBottom: theme.spacing(3),

        [theme.breakpoints.down('xs')]: {
            marginBottom: 0,
        },
    },
    image: {
        marginBottom: theme.spacing(1),
    },
    title: {
        marginBottom: theme.spacing(1),
        outline: 'none',
    },
});

class PageLayout extends React.Component {
    render() {
        const {
            classes, title, children, task, sideContent, showComments,
        } = this.props;

        const labelClasses = task && task.taskLabels ? task.taskLabels.map(d => `${classes.root}_${d}`) : [];

        const hasSideMenu = !!showComments;

        return (
            <>
                {sideContent}

                {showComments && (
                    <ContentCommenting
                        task={task}
                        className={classnames(classes.sideMenu)}
                    />

                )}

                <div className={classnames(classes.root, ...labelClasses, {
                    [classes.withSideMenu]: hasSideMenu,
                })}
                >
                    {title && (
                        <Typography variant="h5" color="primary" className={classes.title} ref={this.titleRef} tabIndex="-1">
                            {title}
                        </Typography>
                    )}

                    {children}
                </div>
            </>
        );
    }
}

PageLayout.propTypes = {
    classes: PropTypes.object.isRequired,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    sideContent: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    sideMenu: PropTypes.object,
    title: PropTypes.string,
    task: PropTypes.object,
    showComments: PropTypes.bool,
};

PageLayout.defaultProps = {
    title: null,
    sideMenu: null,
    task: null,
    sideContent: null,
    showComments: false,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    clientConfig: state.auth.clientConfig,
});

export default connect(mapStateToProps)(withStyles(styles)(PageLayout));
